import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import StyledModalView from '../Modal/StyledModalView';
import './contactPerson.style.css';

function ContactPersonManager({ customerId, contactPersons, onAdd, onUpdate, onDelete }) {
    const { t } = useTranslation();
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [contactToDelete, setContactToDelete] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        position: '',
        is_primary: false
    });

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const contactPersonData = {
            ...formData,
            customer_id: customerId
        };

        if (editingId) {
            await onUpdate(editingId, contactPersonData);
            setEditingId(null);
        } else {
            await onAdd(contactPersonData);
            setIsAdding(false);
        }

        setFormData({
            name: '',
            email: '',
            phone: '',
            position: '',
            is_primary: false
        });
    };

    const handleEdit = (contactPerson) => {
        setFormData({
            name: contactPerson.name,
            email: contactPerson.email || '',
            phone: contactPerson.phone || '',
            position: contactPerson.position || '',
            is_primary: contactPerson.is_primary || false
        });
        setEditingId(contactPerson.id);
        setIsAdding(true);
    };

    const handleCancel = () => {
        setIsAdding(false);
        setEditingId(null);
        setFormData({
            name: '',
            email: '',
            phone: '',
            position: '',
            is_primary: false
        });
    };

    const requestDelete = (contactPerson) => {
        setContactToDelete(contactPerson);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (contactToDelete) {
            await onDelete(contactToDelete.id);
            setShowDeleteModal(false);
            setContactToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setContactToDelete(null);
    };

    return (
        <div className="contact-person-manager">
            <div className="contact-person-header">
                <h3>{t('customers.contactPersons')}</h3>
                {!isAdding && (
                    <button
                        type="button"
                        className="btn btn-add"
                        onClick={() => setIsAdding(true)}
                    >
                        {t('customers.addContactPerson')}
                    </button>
                )}
            </div>

            {isAdding && (
                <div className="add-employee-card">
                    <h3>{editingId ? t('customers.editContactPerson') : t('customers.addContactPerson')}</h3>
                    <form onSubmit={handleSubmit} className="employee-form-grid">
                        <div className="form-group-modern">
                            <label>{t('customers.contactName')} *</label>
                            <input
                                type="text"
                                name="name"
                                className="form-input"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                placeholder={t('customers.contactNamePlaceholder')}
                            />
                        </div>

                        <div className="form-group-modern">
                            <label>{t('customers.contactPosition')}</label>
                            <input
                                type="text"
                                name="position"
                                className="form-input"
                                value={formData.position}
                                onChange={handleInputChange}
                                placeholder={t('customers.contactPositionPlaceholder')}
                            />
                        </div>

                        <div className="form-group-modern">
                            <label>{t('customers.contactEmail')}</label>
                            <input
                                type="email"
                                name="email"
                                className="form-input"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder={t('customers.contactEmailPlaceholder')}
                            />
                        </div>

                        <div className="form-group-modern">
                            <label>{t('customers.contactPhone')}</label>
                            <input
                                type="tel"
                                name="phone"
                                className="form-input"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder={t('customers.contactPhonePlaceholder')}
                            />
                        </div>

                        <div className="form-group-modern">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="is_primary"
                                    checked={formData.is_primary}
                                    onChange={handleInputChange}
                                />
                                <span>{t('customers.setPrimaryContact')}</span>
                            </label>
                        </div>

                        <div className="employee-form-actions">
                            <button type="submit" className="btn btn-accept">
                                {editingId ? t('common.save') : t('common.add')}
                            </button>
                            <button type="button" className="btn btn-cancel" onClick={handleCancel}>
                                {t('common.cancel')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {contactPersons && contactPersons.length > 0 ? (
                <div className="employees-grid">
                    {contactPersons.map(person => (
                        <div key={person.id} className="employee-card">
                            <div className="employee-card-header">
                                <div>
                                    <div className="employee-name">
                                        {person.name}
                                        {person.is_primary && (
                                            <span className="primary-badge">{t('customers.primary')}</span>
                                        )}
                                    </div>
                                    {person.position && (
                                        <div className="employee-role">{person.position}</div>
                                    )}
                                </div>
                            </div>

                            <div className="employee-details">
                                {person.email && (
                                    <div className="employee-detail-row">
                                        <span className="employee-detail-label">{t('common.email')}</span>
                                        <span className="employee-detail-value">
                                            <a href={`mailto:${person.email}`}>{person.email}</a>
                                        </span>
                                    </div>
                                )}
                                {person.phone && (
                                    <div className="employee-detail-row">
                                        <span className="employee-detail-label">{t('customers.contactPhone')}</span>
                                        <span className="employee-detail-value">
                                            <a href={`tel:${person.phone}`}>{person.phone}</a>
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="employee-actions-card">
                                <button
                                    type="button"
                                    className="btn btn-edit"
                                    onClick={() => handleEdit(person)}
                                >
                                    {t('common.edit')}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-delete"
                                    onClick={() => requestDelete(person)}
                                >
                                    {t('common.delete')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                !isAdding && (
                    <div className="no-employees-message">
                        {t('customers.noContactPersons')}
                    </div>
                )
            )}

            {/* Delete Confirmation Modal */}
            <StyledModalView
                isOpen={showDeleteModal}
                onClose={cancelDelete}
                title={t('customers.deleteContactPerson')}
                confirmText={t('common.delete')}
                closeText={t('common.cancel')}
                onConfirm={confirmDelete}
            >
                <p>
                    {t('customers.confirmDeleteContactPerson', {
                        name: contactToDelete?.name
                    })}
                </p>
            </StyledModalView>
        </div>
    );
}

ContactPersonManager.propTypes = {
    customerId: PropTypes.number.isRequired,
    contactPersons: PropTypes.array,
    onAdd: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

export default ContactPersonManager;

